// The premises census folded on the client: every door event of one facility
// night, and who is inside at any second of it. The page scrubs through the
// night by moving a pointer over the events; only the events crossed are
// applied or undone, so a drag costs what it crosses, never the whole night.

export interface NightPayload {
  on: string;
  startsAt: string;
  seconds: number;
  nowSeconds: number | null;
  live: boolean;
  timeZone: string;
  rollCallAt: number | null;
  at: number | null;
}

export interface PersonPayload {
  id: number;
  bed: boolean;
  onPass: boolean;
  insideAtStart: boolean;
}

// personIdx, direction, seconds into the night, event id, overridden, scanned
export type EventTuple = [number, 1 | -1, number, number, 0 | 1, 0 | 1];

export interface Payload {
  night: NightPayload;
  people: PersonPayload[];
  events: EventTuple[];
}

// What one broadcast carries.
export interface IncomingEvent {
  id: number;
  personId: number;
  dir: 1 | -1;
  t: number;
  night: string;
  overridden: boolean;
  method: "tap" | "scan";
  bed: boolean;
  onPass: boolean;
}

// One applied event, newest first, for the door-log excerpt.
export interface Recent {
  personId: number;
  dir: 1 | -1;
  t: number;
  id: number;
  overridden: boolean;
  method: "tap" | "scan";
}

export interface Flip {
  personId: number;
  inside: boolean;
  since: number | null;
}

export interface Reading {
  seconds: number;
  census: number;
  eventsCount: number;
  ins: number;
  outs: number;
  overrides: number;
  peak: number;
  peakAt: number;
  bedHolders: number;
  bedInside: number;
  servicesInside: number;
  outWithBed: number;
  onPassOut: number;
}

const grow = (array: Int32Array, length: number): Int32Array => {
  if (array.length >= length) return array;
  const bigger = new Int32Array(Math.max(length, array.length * 2));
  bigger.set(array);
  return bigger;
};

export class PremisesCensus {
  readonly night: NightPayload;
  readonly people: PersonPayload[];
  readonly events: EventTuple[];

  private indexById = new Map<number, number>();
  private eventIds = new Set<number>();
  // Per person: the event currently applied to them, -1 for none.
  private applied: Int32Array;
  // Per person: inside right now, and the second they came in.
  private inside: Uint8Array;
  private since: Int32Array;
  // Per event: the same person's previous event, -1 for none.
  private previous: Int32Array;
  // Prefix arrays, index i = the state after i events.
  private census: Int32Array;
  private ins: Int32Array;
  private overrides: Int32Array;
  private peak: Int32Array;
  private peakAt: Int32Array;
  private lastForPerson: Int32Array;

  private pointer = 0;
  private bedHolders = 0;
  private bedInside = 0;
  private onPassOut = 0;

  constructor(payload: Payload) {
    this.night = payload.night;
    this.people = payload.people.slice();
    this.events = payload.events.slice();
    const n = this.people.length;
    this.applied = new Int32Array(n).fill(-1);
    this.inside = new Uint8Array(n);
    this.since = new Int32Array(n);
    this.lastForPerson = new Int32Array(n).fill(-1);
    this.previous = new Int32Array(0);
    this.census = new Int32Array(1);
    this.ins = new Int32Array(1);
    this.overrides = new Int32Array(1);
    this.peak = new Int32Array(1);
    this.peakAt = new Int32Array(1);
    this.people.forEach((person, index) => this.seed(person, index));
    this.events.forEach((event, index) => this.index(event, index));
  }

  get length(): number {
    return this.events.length;
  }

  get position(): number {
    return this.pointer;
  }

  // The last second the night has data for: now while it runs, its end once closed.
  get horizon(): number {
    return this.night.nowSeconds ?? this.night.seconds;
  }

  // The census after every event, for drawing the whole night once.
  censusAfter(eventCount: number): number {
    return this.census[eventCount];
  }

  // The highest the census gets after any event, so the chart's scale holds a
  // peak the night has already come down from.
  get peakCensus(): number {
    return this.peak[this.events.length];
  }

  // Appends a broadcast event. Events arrive in the order they were recorded,
  // so nothing before the pointer changes; a clock a hair behind the last event
  // is nudged onto it so the fold stays ordered.
  ingest(event: IncomingEvent): boolean {
    // The tap's own reply and the cable both deliver the same event.
    if (event.night !== this.night.on || this.eventIds.has(event.id)) return false;
    let index = this.indexById.get(event.personId);
    if (index === undefined) {
      index = this.people.length;
      this.people.push({ id: event.personId, bed: event.bed, onPass: event.onPass, insideAtStart: false });
      this.growPeople();
      this.seed(this.people[index], index);
    }
    const last = this.events[this.events.length - 1];
    const t = last ? Math.max(event.t, last[2]) : event.t;
    const tuple: EventTuple = [index, event.dir, t, event.id, event.overridden ? 1 : 0, event.method === "scan" ? 1 : 0];
    this.events.push(tuple);
    this.index(tuple, this.events.length - 1);
    return true;
  }

  // Moves to `seconds`, applying or undoing the events crossed. Returns each
  // person whose standing changed, at their final state.
  moveTo(seconds: number): Flip[] {
    const target = this.countThrough(seconds);
    const touched = new Set<number>();
    while (this.pointer < target) {
      touched.add(this.apply(this.pointer));
      this.pointer += 1;
    }
    while (this.pointer > target) {
      this.pointer -= 1;
      touched.add(this.undo(this.pointer));
    }
    return Array.from(touched, (index) => this.flipFor(index));
  }

  // The last `count` events at or before the pointer, newest first.
  recent(count: number): Recent[] {
    const out: Recent[] = [];
    for (let i = this.pointer - 1; i >= 0 && out.length < count; i -= 1) {
      const [person, dir, t, id, overridden, scanned] = this.events[i];
      out.push({ personId: this.people[person].id, dir, t, id, overridden: overridden === 1, method: scanned === 1 ? "scan" : "tap" });
    }
    return out;
  }

  flipFor(index: number): Flip {
    const inside = this.inside[index] === 1;
    return { personId: this.people[index].id, inside, since: inside ? this.since[index] : null };
  }

  isInside(personId: number): boolean {
    const index = this.indexById.get(personId);
    return index !== undefined && this.inside[index] === 1;
  }

  standingOf(personId: number): Flip {
    const index = this.indexById.get(personId);
    return index === undefined ? { personId, inside: false, since: null } : this.flipFor(index);
  }

  reading(seconds: number): Reading {
    const p = this.pointer;
    const census = this.census[p];
    return {
      seconds,
      census,
      eventsCount: p,
      ins: this.ins[p],
      outs: p - this.ins[p],
      overrides: this.overrides[p],
      peak: this.peak[p],
      peakAt: this.peakAt[p],
      bedHolders: this.bedHolders,
      bedInside: this.bedInside,
      servicesInside: census - this.bedInside,
      outWithBed: this.bedHolders - this.bedInside,
      onPassOut: this.onPassOut,
    };
  }

  // The census as a step path over the whole night, drawn once. `through` is
  // the last second drawn; the y axis tops out at `yMax`.
  stepPath(through: number, yMax: number, width = 1000, top = 8, baseline = 92): string {
    const x = (t: number) => ((t / this.night.seconds) * width).toFixed(1);
    const y = (c: number) => (baseline - (Math.max(0, c) / Math.max(1, yMax)) * (baseline - top)).toFixed(1);
    let d = `M0 ${y(this.census[0])}`;
    for (let i = 0; i < this.events.length && this.events[i][2] <= through; i += 1) {
      d += ` H${x(this.events[i][2])} V${y(this.census[i + 1])}`;
    }
    return `${d} H${x(through)}`;
  }

  // The number of events at or before `seconds`.
  countThrough(seconds: number): number {
    let low = 0;
    let high = this.events.length;
    while (low < high) {
      const mid = (low + high) >> 1;
      if (this.events[mid][2] <= seconds) low = mid + 1;
      else high = mid;
    }
    return low;
  }

  private seed(person: PersonPayload, index: number): void {
    this.indexById.set(person.id, index);
    if (person.bed) this.bedHolders += 1;
    if (person.insideAtStart) {
      this.inside[index] = 1;
      this.since[index] = 0;
      if (person.bed) this.bedInside += 1;
      this.census[0] += 1;
      this.peak[0] = this.census[0];
    } else if (person.bed && person.onPass) {
      this.onPassOut += 1;
    }
  }

  private growPeople(): void {
    const n = this.people.length;
    const applied = new Int32Array(n).fill(-1);
    applied.set(this.applied);
    this.applied = applied;
    const inside = new Uint8Array(n);
    inside.set(this.inside);
    this.inside = inside;
    const since = new Int32Array(n);
    since.set(this.since);
    this.since = since;
    const last = new Int32Array(n).fill(-1);
    last.set(this.lastForPerson);
    this.lastForPerson = last;
  }

  private index(event: EventTuple, i: number): void {
    const [person, dir, t, id, overridden] = event;
    this.eventIds.add(id);
    this.previous = grow(this.previous, i + 1);
    this.previous[i] = this.lastForPerson[person];
    this.lastForPerson[person] = i;
    this.census = grow(this.census, i + 2);
    this.ins = grow(this.ins, i + 2);
    this.overrides = grow(this.overrides, i + 2);
    this.peak = grow(this.peak, i + 2);
    this.peakAt = grow(this.peakAt, i + 2);
    // Only a change of standing moves the census: an override that repeats
    // the person's direction is a correction of the log, not a second arrival.
    const wasInside = this.previous[i] >= 0 ? this.events[this.previous[i]][1] === 1 : this.people[person].insideAtStart;
    this.census[i + 1] = this.census[i] + (dir === 1 ? (wasInside ? 0 : 1) : (wasInside ? -1 : 0));
    this.ins[i + 1] = this.ins[i] + (dir === 1 ? 1 : 0);
    this.overrides[i + 1] = this.overrides[i] + overridden;
    if (this.census[i + 1] > this.peak[i]) {
      this.peak[i + 1] = this.census[i + 1];
      this.peakAt[i + 1] = t;
    } else {
      this.peak[i + 1] = this.peak[i];
      this.peakAt[i + 1] = this.peakAt[i];
    }
  }

  private apply(i: number): number {
    const [person, dir, t] = this.events[i];
    this.applied[person] = i;
    this.setInside(person, dir === 1, t);
    return person;
  }

  private undo(i: number): number {
    const [person] = this.events[i];
    const before = this.previous[i];
    this.applied[person] = before;
    if (before >= 0) this.setInside(person, this.events[before][1] === 1, this.events[before][2]);
    else this.setInside(person, this.people[person].insideAtStart, 0);
    return person;
  }

  private setInside(person: number, inside: boolean, at: number): void {
    const was = this.inside[person] === 1;
    const { bed, onPass } = this.people[person];
    if (inside && !was) {
      if (bed) this.bedInside += 1;
      if (bed && onPass) this.onPassOut -= 1;
    } else if (!inside && was) {
      if (bed) this.bedInside -= 1;
      if (bed && onPass) this.onPassOut += 1;
    }
    this.inside[person] = inside ? 1 : 0;
    if (inside) this.since[person] = at;
  }
}
